import * as R from 'ramda'
import { NominatimJS } from 'nominatim-js'
import { from, merge } from 'rxjs'
import { switchMap, map, scan, filter } from 'rxjs/operators'

import selectSelectedText from '@newswebsiteapp/shared/lib/selectSelectedText'
import selectSelectedValue from '@newswebsiteapp/shared/lib/selectSelectedValue'
import selectChanges from '@newswebsiteapp/shared/lib/selectChanges'
import inputChanges from '@newswebsiteapp/shared/lib/inputChanges'

/// //////
function inputValue(input$) {
  return input$.val()
}

/// /////
const liftPathEqToTop = (path, eqTo, list) =>
  R.sort((a, b) => {
    const isAEq = R.path(path, a) === eqTo
    const isBEq = R.path(path, b) === eqTo

    if (isAEq && isBEq) {
      return 0
    }
    if (isAEq) {
      return -1
    }
    if (isBEq) {
      return 1
    }
    return 0
  }, list)

// NOTE:
// prefill all fields that are empty OR were filled by autocomplete based on user-entered data

export default function setupAddressFieldsGeocoderAutocomplete({
  validator,
  countryInput,
  stateInput,
  zipcodeInput,
}) {
  // console.log(countryInput)
  // console.log(stateInput)
  // console.log(zipcodeInput)

  if (countryInput.length === 0) {
    throw new Error('countryInput emtpy')
  }
  if (stateInput.length === 0) {
    throw new Error('stateInput emtpy')
  }
  if (zipcodeInput.length === 0) {
    throw new Error('zipcodeInput emtpy')
  }

  let ingoreNextUserModifiedInputState = false

  const countryChanges = selectChanges(countryInput)
  const stateChanges = selectChanges(stateInput)
  const zipcodeChanges = inputChanges(zipcodeInput)

  // https://github.com/ReactiveX/rxjs/blob/master/doc/tutorial/applications.sm
  const userModifiedInputState = merge(
    countryChanges.pipe(
      map(() => ({
        user_modified_country: true,
        user_modified_state: false,
      })),
    ),
    stateChanges.pipe(map(() => ({ user_modified_state: true }))),
    zipcodeChanges.pipe(
      map(() => ({
        user_modified_zipcode: true,
        user_modified_country: false,
        user_modified_state: false,
      })),
    ),
  ).pipe(
    filter((_) => {
      if (ingoreNextUserModifiedInputState) {
        ingoreNextUserModifiedInputState = false
        return false
      }
      return true
    }),
    scan((state, update) => R.mergeLeft(state, update), {
      user_modified_country: !!selectSelectedText(countryInput),
      user_modified_state: !!selectSelectedText(stateInput),
      user_modified_zipcode: !!inputValue(zipcodeInput),
    }),
  )

  userModifiedInputState
    .pipe(
      switchMap(
        ({
          user_modified_country,
          user_modified_state,
          user_modified_zipcode,
        }) => {
          const country = selectSelectedText(countryInput)
          const state = selectSelectedText(stateInput)
          const zipcode = inputValue(zipcodeInput)

          // user entered data is the truth, leave it
          // data entered by this script should be removed from query (if user entered something to input or page is rendered with this input prefilled, then user_modified_XXX = true)
          // empty data should be removed from query

          const query = {
            country: user_modified_country ? country : null,
            state: user_modified_state ? state : null,
            postalcode: user_modified_zipcode ? zipcode : true,
          }

          const promise = NominatimJS.search({
            addressdetails: true,
            'accept-language': 'en',
            ...query,
          })

          return from(promise)
        },
        (
          { user_modified_country, user_modified_state, user_modified_zipcode },
          resp,
        ) => ({
          user_modified_country,
          user_modified_state,
          user_modified_zipcode,
          resp,
        }),
      ),
    )
    .subscribe({
      next: ({
        user_modified_country,
        user_modified_state,
        user_modified_zipcode,
        resp,
      }) => {
        // show hints
        // if (R.isEmpty(resp)) {
        //   const key = zipcodeInput.attr("name")
        //   validator.showErrors({
        //     [key]: "zipcode is invalid, no matching locations are found",
        //   })

        //   return
        // }

        if (resp.length > 1) {
          const key = zipcodeInput.attr('name')
          validator.showErrors({
            [key]:
              'several locations match to zipcode, prefilling the most likely',
          })
        } else {
          validator.resetForm()
        }

        // sort - USA country are top priority
        const resp_ = liftPathEqToTop(['address', 'country_code'], 'us', resp)

        // prefill inputs
        const firstAddress = R.path([0, 'address'], resp_)

        if (!firstAddress) return

        const { country_code, state } = firstAddress

        if (!user_modified_country && country_code) {
          const currentStateVal = R.toUpper(selectSelectedValue(countryInput))
          const foundStateVal = R.toUpper(country_code)

          if (currentStateVal !== foundStateVal) {
            countryInput.val(foundStateVal)

            // trigger country-region-selector update
            // https://github.com/benkeen/country-region-selector/blob/7dfa3da64b4e01d801e3516d233cf85066f4179c/source/source-jquery.crs.js#L97-L99

            ingoreNextUserModifiedInputState = true
            countryInput.change()

            // TODO: update without change, because it triggers userModifiedInputState
          }
        }

        if (!user_modified_state && state) {
          setTimeout(() => {
            const matchingStateVal = stateInput
              .find(`option:contains(${state})`)
              .expectLength()
              .val()

            // if can't find matchingStateVal, try find only first word
            let matchingStateVal_ = null

            if (matchingStateVal) {
              matchingStateVal_ = matchingStateVal
            } else {
              const firstStateWord = R.head(R.split(' ', state))
              matchingStateVal_ = stateInput
                .find(`option:contains(${firstStateWord})`)
                .expectLength()
                .val()
            }

            if (!matchingStateVal_) return

            stateInput.val(matchingStateVal_)

            // stateInput.change()
          }, 0)
        }
      },
    })
}
